import React, { FC, memo, useEffect, useMemo } from 'react'
import Head from 'next/head'
import { PageProps, ResponseLocals } from '../common/types'
import { GetServerSideProps } from '../redux/types'
import { getLocaleFromLanguage, getMessagesFromLanguage } from '../i18n/utils'
import { deleteSession } from '../utils/delete-session'
import { getRoutesFromDomain } from '../routes/utils'
import { BaseTransport } from '../transports'
import { useUserDetails } from '../hooks/users'
import Page from '../components/common/page'
import HomePage from '../components/home'
import { pageClient } from '../clients'
import HomePageTransport from '../transports/home-page-transport'
import { useTranslation } from '../hooks/locale'
import { getOrganizationSchemaDetail, getFAQSchemaHTML } from '../schema/common'
import { getWebsiteSchemaHTML } from '../schema/home-page'
import { useSetWishlistCount } from '../hooks/wishlist'
import { generateHeroImagePreloadLinks } from '../components/common/hero-section/utils'
import { HOME_BACKGROUND_IMAGES } from '../components/common/hero-section/constants'

type Props = {
    sitePathname: string
    fullUrl: string
    locale: string
    data: BaseTransport<HomePageTransport>
} & PageProps

const PRECONNECT_DOMAINS = ['https://storage.googleapis.com']

const HomePageContainer: FC<Props> = ({ sitePathname, fullUrl, data }) => {
    const homeData = data?.data
    const { userDetails } = useUserDetails({ additionalDetails: true })
    const isLoggedIn = !!userDetails?.id
    const { t } = useTranslation()
    const organizationSchema = useMemo(
        () => getOrganizationSchemaDetail(t, homeData?.topRatedObjects ?? undefined),
        [t, homeData?.topRatedObjects],
    )

    const websiteSchema = useMemo(() => getWebsiteSchemaHTML(t), [t])
    const faqSchema = useMemo(
        () => getFAQSchemaHTML(homeData?.staticData?.faqSection?.questionAnswers),
        [homeData?.staticData?.faqSection?.questionAnswers],
    )

    const heroImagePreloadLinks = useMemo(() => generateHeroImagePreloadLinks(HOME_BACKGROUND_IMAGES), [])
    const { setWishlistCount } = useSetWishlistCount()

    useEffect(() => {
        setWishlistCount()
    }, [isLoggedIn, setWishlistCount, userDetails])

    if (!homeData) {
        return null
    }

    return (
        <Page head={homeData.head} imagePreloadLinks={heroImagePreloadLinks} preconnectDomains={PRECONNECT_DOMAINS}>
            <Head>
                {!!organizationSchema && (
                    <script type="application/ld+json" dangerouslySetInnerHTML={organizationSchema} />
                )}
                {!!websiteSchema && <script type="application/ld+json" dangerouslySetInnerHTML={websiteSchema} />}
                {!!faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={faqSchema} />}
            </Head>
            <HomePage
                userDetails={userDetails}
                isLoggedIn={isLoggedIn}
                sitePathname={sitePathname}
                url={fullUrl}
                homeDetails={homeData}
            />
        </Page>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req, res }) => {
    const { user, device } = res.locals as ResponseLocals
    const sitePathname = req.path
    const requestUserConfig = deleteSession(user)
    const protocol = req.header('x-forwarded-proto') || req.protocol || 'http'
    const host = req.headers['host']
    const fullUrl = `${protocol}://${host}${sitePathname}`
    const locale = getLocaleFromLanguage(user.language)

    const data = await pageClient.getHomePage({ user })
    if (data.status === 404) {
        return {
            notFound: true,
        }
    }
    return {
        props: {
            device,
            requestUserConfig: requestUserConfig,
            messages: await getMessagesFromLanguage(user.language),
            routes: await getRoutesFromDomain(user.domain),
            sitePathname,
            fullUrl,
            locale,
            data,
        },
    }
}

export default memo(HomePageContainer)
